/* ================================================================ */
// CESIUM CONTAINER | CesiumContainer.jsx
/* ================================================================ */
/* ========================================== */
// Imports
/* ========================================== */
import React, {useEffect, useRef, useState, useImperativeHandle, forwardRef, useCallback} from 'react';
import PropTypes from 'prop-types';
import {connect, useDispatch} from 'react-redux';
import {bindActionCreators} from 'redux';
import * as Cesium from 'cesium';
import useReactRouter from 'use-react-router';
import { isNullOrUndefined } from 'util';
// Actions
import { updateMapViewState, updateMapStateById } from '../../actions/Map';
import { fetchStates } from '../../actions/Data/Mapping';
import { fetchPanels, updateDodgyVislayers } from '../../actions/Data/Content';
// Selectors
import {getStatesByHash, getLoadingStatus, getDefaultState} from '../../selectors/Data';
import {getViewState, getMapLoadingStatus, getActiveLayers} from '../../selectors/Map';
//import 'cesium/Build/Cesium/Widgets/widgets.css';
import makeStyles from '@material-ui/core/styles/makeStyles';
import Button from '@material-ui/core/Button';
import Dashboard from './Dashboard';
//import CzmlDataSource from './CZMLDataSource';
import { styleVisLayer, getStyle, setDefaultStyle, saveCZML } from './StyleUtils';
/* ========================================== */
// Style Settings
/* ========================================== */
const useStyles = makeStyles(theme => ({
    root: {
        position: 'relative',
        backgroundColor: theme.palette.secondary.main,
        width: '100%',
        height: '100%',
        margin: 0,
        padding: 0,
        overflow: 'hidden'
    },
    loadingContainer: {
        height: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
    },
    button: {
        position: 'absolute'
    },
    credits: {
        position: 'absolute',
        right: 0,
        bottom: 0,
        padding: '1em',
        width: '15vw',
        height: '3em',
        display: 'flex',
        zIndex: 1,
        justifyContent: 'space-between',
        '& img': {
            objectFit: 'contain',
            width: '50%'
        }
    }
}));
const home = {
    "destination": {
        "x": -5002965.10882716,
        "y": 2519368.2360078953,
        "z": -3083740.637290964
    },
    "duration": 10,
    "orientation": {
        "heading": 1.1542992823390623,
        "pitch": -0.5273718484770713,
        "roll": 6.280199761841466
    }
};
const flyto = {
    "destination": {
        "x": -5005722.567225711,
        "y": 2521175.9809504044,
        "z": -3040682.6595210237
    },
    "duration": 40,
    "orientation": {
        "heading": 2.26209360947846,
        "pitch": -0.6571416700228601,
        "roll": 6.280436854687304
    }
};

/* ========================================== */
/**
 * Cesium Container
 * ----------------
 * Container for Cesium component
 * linked to the MapReducer
 *
 * @param {object} props component props
 */
const CesiumContainer = (props) => {
    // THIS CONTAINER SHOULD FETCH CESIUM CONFIG, ION TOKEN,
    // AND MAP STATES
    const {
        viewState
    } = props;
    const classes = useStyles();
    const [datasets, setDatasets] = useState([]);
    const [visLayers, setVisLayers] = useState([]);
    const [sortedArray, setSortedArray] = useState([]);
    const childRef = useRef();
    const dispatch = useDispatch();
    const {match} = useReactRouter();
    const [localViewState, setViewState] = useState([]);

    useEffect(() => {
        if (match.params.category && match.params.category!="admin" && props.viewState.visLayers && props.viewState.visLayers !== localViewState) {
            setViewState(props.viewState.visLayers);
            var vl = props.viewState.visLayers.map( (Id) => {
                var split = Id.split('/');
                var Name = split[split.length-1];                
                return { style: "", Name, Filename: Name, Id, Display: true, Visible: true, type: "CZML" }; 
            });
            //setSortedArray(vl.map( s => s.Name).reverse());
            setVisLayers(vl);        
        }
        else if (props.viewState.visLayers && props.viewState.visLayers !== localViewState) {
            setViewState(props.viewState.visLayers);
            var vl = visLayers.map( (v) => {
                if (props.viewState.visLayers.includes(v.Id)) 
                    v.Visible = true;
                else
                    v.Visible = false;
                return v;
            })
            vl.sort( (a,b) => {
                var aIndex = props.viewState.visLayers.findIndex( f => f === a.Id);
                var bIndex = props.viewState.visLayers.findIndex( f => f === b.Id);
                if (aIndex !== -1 && bIndex !== -1)
                    return aIndex - bIndex;
                else if (aIndex !== -1)
                    return -1;
                else if (bIndex !== -1)
                    return 1;
                else
                    return 0;
            })
            //handleSorted(vl);
            //setSortedArray(vl.map( s => s.Name).reverse());
            setVisLayers(vl);
        }
    }, [props.viewState, visLayers])

    useEffect(() => {
        dispatch(updateDodgyVislayers(visLayers));
    }, [visLayers])

    useEffect(()=>{
        //console.debug(`${CesiumContainer.displayName} | `, 'componentDidUpdate - ', 'layerData:', props.layerData);
        if(!props.isLoading) {
            // > Load State from DB (if not already loaded)
            //this.props.fetchStates([defaultState]);
            // > Apply state to map
            //const defaultState = this.props.states.byHash//byId.find(state=>state.id === defaultStateID);
            const defaultState = props.defaultState || 'default';
            props.updateMapStateById(defaultState);
        }
    }, [props.isLoading]);

    function triggerFlyTo() {
        setViewState(flyto);
    }

    function triggerHome() {
        handleViewState(home);
    }

    /* ========================================== */
    /**
     * Handle View State
     * -----------------
     * Sends view state update to MapReducer
     *
     * @param {object} viewState current view state
     */
    /* ========================================== */
    function handleViewState(viewState) {
        if(props.updateMapViewState) {
            //console.debug(`${CesiumComponent.displayName} | `, 'handleViewState -', 'viewState:', viewState);
            props.updateMapViewState(viewState);
        }
    }

    function handleLoadDataset(dataset) {
        console.log("LOADING:", dataset);
        var Filename = dataset.Name.split('.')[0]+".czml";
        setVisLayers([{...dataset, Filename, Visible: true, type: 'data'}, ...visLayers ]);
    }

    function handleSave(id,filename) {
        var vis = visLayers.find( v => v.Id === id);
        childRef.current.saveCZML(vis,filename);
    }

    function handleStyle(id, style) {
        var vis = visLayers.find( v=> v.Id === id);
        var styleJSON = JSON.parse(style);
        if (vis) {
            childRef.current.styleVisLayer({visLayerName: vis.Name, styleJSON});
            vis.Name = styleJSON.name;
            setVisLayers(
                visLayers.map((v) => {
                  return v.Id === id ? vis : v
                })
            )
        }

    }

    function handleLayerToggle(id, isChecked) {
        setVisLayers(visLayers.map( v => {
            if (v.Id === id)
                v.Visible = !v.Visible;
            return v;
        }));
    }

    function handleDeleteDataset(id) {
        setDatasets(datasets.filter(v => v.Id !== id));
    }

    function handleDeleteVisLayer(id) {
        setVisLayers(visLayers.filter(v => v.Id !== id));
    }

    function handleSorted(sortedArr) {
        //setVisLayers(sortedArr);
        setSortedArray(sortedArr.map( s => s.Name).reverse());
        setVisLayers(sortedArr);
    }

    function handleLayersUpdated(lyrs) {
        if (lyrs !== visLayers)
            setVisLayers(lyrs);
    }

    useEffect( () => {
        var geojsonUrl = 'https://api.myglobe.app/rousuploads?path=geojson';
        var czmlUrl = 'https://api.myglobe.app/rousuploads?path=czml';
        fetch(geojsonUrl) // Call the fetch function passing the url of the API as a parameter
        .then((resp) => resp.json())
        .then((data) => {
            var datasetArray = data.map( (Id) => {
                var split = Id.split('/');
                var Name = split[split.length-1];
                var Title = Name.split('.')[0];
                var Id = 'https://rous.myglobe.app'+Id;
                return { Title, Name, Id, Display: true };
            });
            setDatasets(datasetArray);
        })
        .catch(function(e) {
            //TODO #benjob
            console.log("ERROR retrieving datasets:", e);
        });
        const {category} = match.params;             
        if (category=="admin") {
            fetch(czmlUrl) // Call the fetch function passing the url of the API as a parameter
            .then((resp) => resp.json())
            .then((data) => {
                var visArray = data.map( (Id) => {
                    var split = Id.split('/');
                    var Name = split[split.length-1];
                    var Title = Name.split('.')[0];
                    var Id = 'https://myglobe.app'+Id;
                    return { style: "", Name, Filename: Name, Id, Display: true, Visible: false, type: "CZML" };                   
                });
                dispatch(updateDodgyVislayers(visArray));
                setSortedArray(visArray.map( s => s.Name).reverse());
                setVisLayers(visArray);
            })
            .catch(function(e) {
                //TODO #benjob
                console.log("ERROR retrieving datasets:", e);
            });
        }
        //setVisLayers([{style: "", Name: "Pin Template", Id: "/assets/czml/pin_template.czml", Display: true, Visible: true, type: "CZML"}]);
        
        //props.updateMapViewState(home);
    }, [])

    return (
        <div className={classes.root}>
            {
            //isNullOrUndefined(match.params.category) && 
            match.params.category=="admin"&&
                <Dashboard 
                    datasets={datasets} visLayers={visLayers} 
                    handleDeleteVisLayer={handleDeleteVisLayer} handleDeleteDataset={handleDeleteDataset}
                    handleLayerToggle={handleLayerToggle} handleStyle={handleStyle} 
                    handleLoadDataset={handleLoadDataset} handleSave={handleSave} 
                    handleSorted={handleSorted}
                />
             }   
            <CesiumComponent {...props} ref={childRef} sortedArray={sortedArray} viewState={viewState} visLayers={visLayers} onLayersUpdated={handleLayersUpdated} onViewUpdate={handleViewState}/>
            {
                //<Button style={{left: '3em', top: '3em'}} className={classes.button} onClick={triggerHome}>Home</Button>
                //<Button style={{left: '5em', top: '3em'}} className={classes.button} onClick={triggerFlyTo}>FlyTo</Button>
            }
        </div>
    );
};
CesiumContainer.displayName = 'CesiumContainer';
const CesiumComponent = forwardRef((props, ref) => {
    const classes = useStyles();
    const {options, onViewUpdate, onLayersUpdated} = props;
    const cesiumContainer = useRef(null);
    const viewer = useRef(null);
    const [viewState, setViewState] = useState(props.viewState);
    const [visLayers,setVisLayers] = useState(props.visLayers);
    const previousVisLayers = usePrevious(visLayers);
    const cancelList = useRef([]);
    const {match} = useReactRouter();

    function usePrevious(value) {
        // The ref object is a generic container whose current property is mutable ...
        // ... and can hold any value, similar to an instance property on a class
        const ref = useRef();
        
        // Store current value in ref
        useEffect(() => {
          ref.current = value;
        }, [value]); // Only re-run if value changes
        
        // Return previous value (happens before update in useEffect above)
        return ref.current;
      }

    useImperativeHandle(ref, () => ({

        saveCZML(id,filename) {
          saveCZML(id,filename,viewer);
        },
        styleVisLayer(styles) {
            styleVisLayer(styles,viewer);
        }

    }));
    /* ========================================== */
    /**
     * Effect: Init Cesium Viewer
     * --------------------------
     * Setup Cesium settings and viewer
     */
    useEffect(()=>{
        if(cesiumContainer.current) {
            //Cesium.Ion.defaultAccessToken = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJqdGkiOiJjNGQ2ODIxYS03ZDdkLTRiZTctYmQwOC0yMDZlNzc2ODUxODEiLCJpZCI6Mjc4LCJpYXQiOjE1MjUyMTYyNzJ9.KcxEOT5W2jacewSbDmtAc8q6_3IagO5-XZYUTANDwMI';
            const newViewer = new Cesium.Viewer(cesiumContainer.current, {
                ...options
            });

            /*const imageryProvider = new Cesium.UrlTemplateImageryProvider({
                url: "http://131.181.47.39:8080/slippy/aerial50cm/{z}/{x}/{y}.png"
            });/*
            const WMSImageryProvider = new Cesium.WebMapServiceImageryProvider({
                url : 'https://maps.six.nsw.gov.au/arcgis/services/public/NSW_Base_Map/MapServer/WMSServer?version=1.3.0&service=WMS',
                layers : '0',
                tileHeight: 256,
                tileWidth: 256,
                maximumLevel: 18,
                parameters : {
                    transparent : true,
                    tiled : true,
                    format : 'image/png'
                }
            });
            //newViewer.scene.imageryLayers.add(new Cesium.ImageryLayer(WMSImageryProvider, { minimumTerrainLevel: 0 }));
            //newViewer.scene.imageryLayers.addImageryProvider(WMSImageryProvider);*/
            var WMTSImagery = new Cesium.WebMapTileServiceImageryProvider({
                url : 'https://maps1.six.nsw.gov.au/arcgis/rest/services/sixmaps/LPI_Imagery_Best/MapServer/WMTS/',
                layer : 'Best_WebM_Footprint',
                style : 'default',
                format : 'image/png',
                parameters: {
                    transparent : 'true',
                },
                tileMatrixSetID : 'default028mm',
                // tileMatrixLabels : ['default028mm:0', 'default028mm:1', 'default028mm:2' ...],
                //maximumLevel: 19,
                credit : new Cesium.Credit('<a href="https://www.spatial.nsw.gov.au/" target="_blank">DFSI Spatial Services NSW</a>')
            });
            
            var layer = new Cesium.ImageryLayer(WMTSImagery);//,{minimumTerrainLevel:11});
            newViewer.scene.imageryLayers.add(layer);
            layer.colorToAlpha = Cesium.Color.fromBytes(255,255,255);
            layer.colorToAlphaThreshold = 0.05;
            //newViewer.scene.imageryLayers.add(new Cesium.ImageryLayer(imageryProvider,{maximumTerrainLevel:12}));

            if (match.params.category !== 'admin') {
                newViewer.selectedEntityChanged.addEventListener((entity) =>{
                    newViewer.selectedEntity = undefined;
                });
            }

            newViewer.scene.postProcessStages.fxaa.enabled = true;
            //newViewer.scene.screenSpaceCameraController._minimumZoomRate = 200;
            //newViewer.scene.screenSpaceCameraController._maximumZoomRate = 65000;
            newViewer.camera.frustum.near = 0.01;
            //newViewer.scene.globe.preloadSiblings = true;
            newViewer.scene.globe.preloadAncestors = true;
            //newViewer.scene.globe.tileCacheSize = 100000;
            newViewer.clock.currentTime = Cesium.JulianDate.fromDate(new Date(2020,7,3,12,0,0,0));
            newViewer.clock.multiplier = 1;
            newViewer.scene.light = new Cesium.SunLight({color: Cesium.Color.fromBytes(203, 212, 180), intensity: 5.0});

            newViewer.scene.globe.enableLighting = true;
            newViewer.scene.globe.dynamicAtmosphereLighting = true;
            newViewer.scene.globe.dynamicAtmosphereLightingFromSun = true;
            newViewer.scene.globe.maximumScreenSpaceError = 2.0;
            newViewer.scene.logarithmicDepthBuffer = false;
            newViewer.scene.globe.depthTestAgainstTerrain = false;

            newViewer.canvas.style.imageRendering = "auto";
            newViewer.camera.setView(props.viewState);

            newViewer.camera.percentageChanged = 0.01;
            newViewer.camera.changed.addEventListener(handleCameraChange);
            newViewer.camera.changed.addEventListener(function() {
                if (viewer.current.camera._suspendTerrainAdjustment && viewer.current.scene.mode === Cesium.SceneMode.SCENE3D) {
                    viewer.current.camera._suspendTerrainAdjustment = false;
                    viewer.current.camera._adjustHeightForTerrain();
                }
            });
            newViewer.screenSpaceEventHandler.setInputAction((e) => {
                logCamera();
            }, Cesium.ScreenSpaceEventType.RIGHT_CLICK);

            var eventHelper = new Cesium.EventHelper();

            eventHelper.add(WMTSImagery.errorEvent, (err) => {
                console.log(err);
                if (err.error.statusCode === 500) {
                    viewer.current.scene.globe.preloadAncestores = false;
                    viewer.current.scene.imageryLayers.remove(layer);
                    props.dispatch({type: 'UPDATE_MAP_TILES_LOADED'});
                    console.log("ERROR LOADING HIGH RESOLUTION IMAGERY - SHOW GLOBE");
                    eventHelper.removeAll();
                }
            })

            eventHelper.add(newViewer.scene.globe.tileLoadProgressEvent, (data) => {
                if (data === 0) {
                    //done loading data
                    //viewer.current.scene.globe.preloadSiblings = false;
                    viewer.current.scene.globe.preloadAncestores = false;
                    props.dispatch({type: 'UPDATE_MAP_TILES_LOADED'});
                    console.log("FINISHED LOADING TILES");
                    eventHelper.removeAll();
                }
            });

            function stopFlyTo() {
                viewer.current.scene.camera.cancelFlight();
            }

            newViewer.screenSpaceEventHandler.setInputAction(stopFlyTo, Cesium.ScreenSpaceEventType.LEFT_DOWN);
            newViewer.screenSpaceEventHandler.setInputAction(stopFlyTo, Cesium.ScreenSpaceEventType.RIGHT_DOWN);
            newViewer.screenSpaceEventHandler.setInputAction(stopFlyTo, Cesium.ScreenSpaceEventType.MIDDLE_DOWN);
            newViewer.scene.frameState.creditDisplay.addDefaultCredit(new Cesium.Credit(' '),true);
            newViewer.scene.frameState.creditDisplay.addDefaultCredit(new Cesium.Credit('ROUS COUNTY COUNCIL 2020 © ALL RIGHTS RESERVED'),true);
            newViewer.scene.frameState.creditDisplay.addDefaultCredit(new Cesium.Credit('<a href="https://rous.nsw.gov.au/cp_themes/default/page.asp?p=DOC-DSP-20-14-85" target="_blank" title="TERMS OF USE"/>TERMS OF USE</a>'),true);
            newViewer.scene.frameState.creditDisplay.addDefaultCredit(new Cesium.Credit('<a href="https://rous.nsw.gov.au/cp_themes/default/page.asp?p=DOC-SMB-45-30-01" target="_blank" title="PRIVACY POLICY"/>PRIVACY POLICY</a>'),true);
            viewer.current = newViewer;
        }
    }, []);  
    /* ========================================== */
    /**
     * Effect: Map State Update
     * ------------------------
     * Handles incoming view state prop transitions
     * and layer updates
     */
    useEffect(()=>{
        /* ======================== */
        // COMPONENT UPDATE
        /* ======================== */
        if(props.viewState && (
            props.viewState?.destination?.x !== viewState.destination?.x
            || props.viewState?.destination?.y !== viewState.destination?.y
            || props.viewState?.destination?.z !== viewState.destination?.z
            || props.viewState?.orientation?.heading !== viewState.orientation?.heading
            || props.viewState?.orientation?.pitch !== viewState.orientation?.pitch
            || props.viewState?.orientation?.roll !== viewState.orientation?.roll
            || props.viewState?.orientation?.direction !== viewState.orientation?.direction)
        ) {
            //console.debug(`${CesiumComponent.displayName} | `, 'Update(viewState) -', 'props.viewState (new):', props.viewState, 'viewState (old):', viewState);

            const newView = {
                ...viewState,
                ...props.viewState,
                //... (orientation && {orientation})
            };

            setViewState(newView);

            if(props.viewState.duration)
                viewer.current.camera.flyTo({...newView,
                    maximumHeight: 500
                });
            else
                viewer.current.camera.setView(newView);
        }
    }, [props.viewState]);

    function sortDatasets(sorted) {
        //console.log("SORTED:", sorted);
        for (var i = 0; i < sorted.length; i++) {
            var ds = viewer.current.dataSources.getByName(sorted[i])[0];
            if (!ds)
                break;
            var dsIdx = viewer.current.dataSources.indexOf(ds);
            while (i !== dsIdx) {
                if (i > dsIdx)
                    viewer.current.dataSources.raise(ds);
                else
                    viewer.current.dataSources.lower(ds);
                dsIdx = viewer.current.dataSources.indexOf(ds);
            }
            //console.log(ds,dsIdx,sorted[i],i);  
        }
        //console.log(viewer.current.dataSources);
    }

    useEffect( () =>{
        if (viewer.current && props.sortedArray.length === viewer.current.dataSources.length)
            sortDatasets(props.sortedArray);        
    },[props.sortedArray])

    /* ========================================== */
    /**
     * Effect: Vis Layer Update
     * ------------------------
     * Handles incoming view state prop transitions
     * and layer updates
     */
    useEffect(()=>{       
        
        if(viewer.current && props.visLayers !== visLayers) {
            var promises = [];
            setVisLayers(props.visLayers);
            // Unload datasets not in new prop
            var loadedVisLayers = visLayers.map( v => v.Id);
            var newVisLayers = props.visLayers.map( v => v.Id);
            visLayers.filter(visLayer=>!newVisLayers.includes(visLayer.Id)).forEach(visLayer=>{
                var lyr = viewer.current.dataSources.getByName(visLayer.Name)[0];
                if (lyr)
                    viewer.current.dataSources.remove(lyr);
                else
                    cancelList.current = [...cancelList.current, visLayer.Name];
            });
            // Load datasets not in current state
            
            props.visLayers.filter(visLayer=>!loadedVisLayers.includes(visLayer.Id)).forEach(visLayer=>{
                if (visLayer.type==="CZML") {
                    promises.push(Cesium.CzmlDataSource.load(visLayer.Id).then((lyr) => {
                        lyr.entities.values.forEach( entity => 
                            entity.getProperty = function(name) {
                                return entity.properties.getValue(viewer.current.clock.currentTime)[name];
                        })
                        lyr.name = visLayer.Name;
                        var styleDetails = getStyle(lyr,viewer);
                        if (styleDetails)
                        {
                            //visLayer.Name = styleDetails.name;
                            visLayer.style = styleDetails.style;
                        }
                        //if (visLayer.Visible)
                        //    lyr.show = true;
                        //else
                        lyr.show = false;
                        viewer.current.dataSources.add(lyr);
                        return lyr;
                    }));

                } else {
                    promises.push(Cesium.GeoJsonDataSource.load(visLayer.Id).then((lyr) => {
                        lyr.entities.values.forEach( entity => 
                            entity.getProperty = function(name) {
                                return entity.properties.getValue(viewer.current.clock.currentTime)[name];
                        })
                        visLayer.Name = lyr.name;
                        visLayer.style = setDefaultStyle(lyr);
                        styleVisLayer({visLayerName: visLayer.Name, styleJSON: JSON.parse(visLayer.style)},viewer,lyr);
                        viewer.current.dataSources.add(lyr);
                        return lyr;
                    }));
                }
            });
            evaluatePromises(promises);
        }
    }, [props.visLayers]);

    async function evaluatePromises(promises) {
        var data = await Promise.all(promises);
               
        data = data.filter( (d) => {
            if (cancelList.current.includes(d.name)) {
                if (viewer.current.dataSources.contains(d))
                    viewer.current.dataSources.remove(d);
                cancelList.current = cancelList.current.filter( f => f !== d.name);
                return false;
            }
            return true;
        })
        sortDatasets(props.visLayers.map(m => m.Name).reverse());
        var visLayers = props.visLayers.map( m => {
            if (!m.Visible)
            {
                var dataSource = viewer.current.dataSources.getByName(m.Name);
                if (dataSource.length > 0)
                    dataSource[0].show = false;
            }
            else
            {
                var dataSource = viewer.current.dataSources.getByName(m.Name);
                if (dataSource.length > 0)
                    dataSource[0].show = true;
            }
            return m;
        })
        //setVisLayers(visLayers);             
        //onLayersUpdated && onLayersUpdated(visLayers);
    }

    /* ========================================== */
    /**
     * Effect: Map State Update
     * ------------------------
     * Handles incoming view state prop transitions
     * and layer updates
     */

    function handleCameraChange(event) {
      /*  
        if(viewer.current.camera.position.x > -4000000) viewer.current.camera.position.x = -4000000;
        else if(viewer.current.camera.position.x < -6500000) viewer.current.camera.position.x = -6500000;
        else if(viewer.current.camera.position.y < 2500000) viewer.current.camera.position.y = 2500000;
        if(viewer.current.camera.position.y > 3000000) viewer.current.camera.position.y = 3000000;
        
        else if(viewer.current.camera.position.z > -3000000) viewer.current.camera.position.z = -3000000;
        if(viewer.current.camera.position.z < -6000000) viewer.current.camera.position.z = -6000000;

        if(viewer.current.camera.positionCartographic.height < 250) {
            const newDest = Cesium.Cartesian3.fromRadians(
                viewer.current.camera.positionCartographic.longitude,
                viewer.current.camera.positionCartographic.latitude,
                250
            );
            viewer.current.camera.position = newDest;
        }*/

        const currentView = {
            destination: {
                x: viewer.current.camera.position.x,
                y: viewer.current.camera.position.y,
                z: viewer.current.camera.position.z
            },
            orientation: {
                heading: viewer.current.camera.heading,
                pitch: viewer.current.camera.pitch,
                roll: viewer.current.camera.roll
            }
        };        
        //console.debug(`${CesiumComponent.displayName} | `, 'handleCameraChange -', 'currentView:', currentView);
        setViewState(currentView);
        onViewUpdate && onViewUpdate(currentView);
    }

    function logCamera() {
        console.log(JSON.stringify({
            destination: Cesium.Cartographic.toCartesian(viewer.current.camera.positionCartographic),
            duration: 10,
            orientation: {
                heading: viewer.current.camera.heading,
                pitch: viewer.current.camera.pitch,
                roll: viewer.current.camera.roll
            }
        },null,'\t'));
    }

    return (
        <>
            <div key="credits" className={classes.credits}>
                <img alt="ROUS County Council" src="https://rous.myglobe.app/uploads/rous/media/logos/Logos-01.png" />
                <img alt="Future Water Project 2060" src="https://rous.myglobe.app/uploads/rous/media/logos/Logos-02.png" />
            </div>
            <div key="map" className={classes.root} id="cesiumContainer" ref={cesiumContainer}/>
        </>
    );
});

CesiumComponent.propTypes = {
    visLayers: PropTypes.arrayOf(PropTypes.object),
    options: PropTypes.object,
    onViewUpdate: PropTypes.func
};
CesiumComponent.defaultProps = {
    options: {
        fullscreenElement: 'cesiumContainer',
        animation: false,
        baseLayerPicker: false,
        fullscreenButton: false,
        geocoder: false,
        homeButton: false,
        imageryProvider:new Cesium.MapboxImageryProvider({
            mapId: 'mapbox.satellite',
            accessToken: 'pk.eyJ1IjoiaWZldmlzZXIiLCJhIjoiY2tjZWFxbm16MDM5bjJ3bWdwOWttcWl3YSJ9.87MELnyNMU8ew9NLh-swwQ'
        }),
        infoBox: true,
        sceneModePicker: false,
        scene3DOnly: true,
        selectionIndicator: false,
        timeline: false,
        navigationHelpButton: false,
        navigationInstructionsInitiallyVisible: false,
        projectionPicker: false,
        fullscreen: true,
        resolutionScale: 1.0,//window.devicePixelRatio,
        shadows: false,
        terrainExaggeration: 1,
        terrainProvider: Cesium.createWorldTerrain({
            requestVertexNormals: true,
            requestWaterMask: true
        }),
        terrainShadows: Cesium.ShadowMode.ENABLED,
        useBrowserRecommendedResolution: false,
        //useDefaultRenderLoop: false,
        contextOptions:{
            requestWebgl2: false,
            webgl: {
                alpha: false,
                antialias: true,
                preserveDrawingBuffer: false,
                failIfMajorPerformanceCaveat: false,
                depth: true,
                stencil: true
            },
        },
    }
};

/* ========================================== */
// Redux
/* ========================================== */
const mapStateToProps = store => {
    const getAllLoadingStatuses = (store) => {return getLoadingStatus(store, 'globe') || getMapLoadingStatus(store, 'map');};
    return {
        defaultState: getDefaultState(store),
        viewState: getViewState(store),

        layerData: getActiveLayers(store),
        layers: store.Map.layers,

        states: getStatesByHash(store),
        isLoading: getAllLoadingStatuses(store)
    };
};
const mapDispatchToProps = dispatch => {
    return bindActionCreators(Object.assign({updateMapViewState, updateMapStateById, fetchPanels, fetchStates, dispatch}), dispatch);
};
CesiumComponent.displayName = 'Cesium';
/* ========================================== */
// Export
/* ========================================== */
export default connect(mapStateToProps, mapDispatchToProps)(CesiumContainer);